<template>
  <FlotoCrudContainer as-table :fetch-fn="getCsvBatch" :columns="tableColumns">
    <template v-slot:add-controls="{ refreshList }">
      <FlotoPageHeader
        :back-link="backLink"
        use-divider
        :title="
          moduleName === $constants.PRODUCTVENDOR
            ? `${$tc('price')} ${$tc('list')} ${$tc('batch', 2)}`
            : [
                $constants.REQUEST,
                $constants.PROBLEM,
                $constants.CHANGE,
                $constants.RELEASE,
              ].indexOf(moduleName) >= 0 && $route.query.type === 'category'
            ? `${$tc(moduleName)} ${$tc('category')} ${$tc('batch', 2)}`
            : `${$tc(moduleName === $constants.CMDB ? 'ci' : moduleName)} ${$tc(
                'batch',
                2
              )}`
        "
      >
        <template v-slot:after-title>
          <MButton
            id="refresh-btn"
            class="mr-2"
            variant="default"
            @click="refreshList"
          >
            {{ $tc('refresh') }}
          </MButton>
          <div class="inline-flex justify-end">
            <ImportCsvContainer
              v-if="getAssetTypeSystemName[moduleName]"
              :module-name="$constants.ASSET"
              :sub-module-name="moduleName"
              @imported="refreshList"
            >
              <template v-slot:title>
                {{ $tc('import_batch') }}
              </template>
              <template v-slot:trigger="{ open }">
                <MButton
                  id="import-csv-batch-btn"
                  class="mr-2"
                  variant="primary"
                  @click="open"
                >
                  {{ $tc('import_batch') }}
                </MButton>
              </template>
            </ImportCsvContainer>
            <ImportCsvContainer
              v-else-if="
                moduleName === 'requester' || moduleName === 'technician'
              "
              :module-name="$constants.USER"
              :user-type="moduleName"
              @imported="refreshList"
            >
              <template v-slot:title>
                {{ $tc('import_batch') }}
              </template>
              <template v-slot:trigger="{ open }">
                <MButton
                  id="import-csv-batch-btn"
                  class="mr-2"
                  variant="primary"
                  @click="open"
                >
                  {{ $tc('import_batch') }}
                </MButton>
              </template>
            </ImportCsvContainer>
            <ImportCsvContainer
              v-else-if="
                [
                  $constants.REQUEST,
                  $constants.PROBLEM,
                  $constants.CHANGE,
                  $constants.RELEASE,
                ].indexOf(moduleName) >= 0 || $route.query.type === 'category'
              "
              :module-name="$route.query.type || moduleName"
              :ref-model="moduleName"
              @imported="refreshList"
            >
              <template v-slot:title>
                {{ $tc('import_batch') }}
              </template>
              <template v-slot:trigger="{ open }">
                <MButton
                  id="import-csv-batch-btn"
                  class="mr-2"
                  variant="primary"
                  @click="open"
                >
                  {{ $tc('import_batch') }}
                </MButton>
              </template>
            </ImportCsvContainer>
            <ImportCsvContainer
              v-else-if="moduleName === $constants.SERVICE_CATALOG"
              :module-name="$constants.SERVICE_CATALOG"
              :ref-model="$constants.SERVICE_CATALOG"
              @imported="refreshList"
            >
              <template v-slot:title>
                {{ $tc('import_batch') }}
              </template>
              <template v-slot:trigger="{ open }">
                <MButton
                  id="import-csv-batch-btn"
                  class="mr-2"
                  variant="primary"
                  @click="open"
                >
                  {{ $tc('import_batch') }}
                </MButton>
              </template>
            </ImportCsvContainer>
            <ImportCsvContainer
              v-else-if="moduleName === $constants.TASK"
              :module-name="$constants.TASK"
              :ref-model="$constants.TASK"
              :project-id="parseInt($route.query.id)"
              @imported="refreshList"
            >
              <template v-slot:title>
                {{ $tc('import_batch') }}
              </template>
              <template v-slot:trigger="{ open }">
                <MButton
                  id="import-csv-batch-btn"
                  class="mr-2"
                  variant="primary"
                  @click="open"
                >
                  {{ $tc('import_batch') }}
                </MButton>
              </template>
            </ImportCsvContainer>
            <ImportCsvContainer
              v-else
              :module-name="moduleName"
              @imported="refreshList"
            >
              <template v-slot:title>
                {{ $tc('import_batch') }}
              </template>
              <template v-slot:trigger="{ open }">
                <MButton
                  id="import-csv-batch-btn"
                  class="mr-2"
                  variant="primary"
                  @click="open"
                >
                  {{ $tc('import_batch') }}
                </MButton>
              </template>
            </ImportCsvContainer>
          </div>
        </template>
      </FlotoPageHeader>
    </template>

    <template v-slot:batchNumber="{ item }">
      <td class="resource-link">
        {{ item.batchNumber }}
      </td>
    </template>
    <template v-slot:createdBy="{ item }">
      <td class="resource-link">
        <FlotoTechnicianPicker :value="item.createdBy" disabled />
      </td>
    </template>
    <template v-slot:status="{ item }">
      <td class="text-ellipsis">
        {{
          (importCsvStatusOptions.find((i) => i.key === item.status) || {}).text
        }}
      </td>
    </template>
    <template v-slot:createdTime="{ item }">
      <td class="text-ellipsis">
        {{ item.createdTime | datetime }}
      </td>
    </template>
    <template v-slot:endTime="{ item }">
      <td class="text-ellipsis">
        <span v-if="item.endTime">
          {{ item.endTime | datetime }}
        </span>
        <span v-else>
          {{ '---' }}
        </span>
      </td>
    </template>
    <template v-slot:processingTime="{ item }">
      <td class="text-ellipsis">
        <span v-if="item.processingTime > 0">
          {{ item.processingTime | duration }}
        </span>
        <span v-else>
          {{ '---' }}
        </span>
      </td>
    </template>
    <template v-slot:discoveryType="{ item }">
      <td class="text-ellipsis">
        {{
          (discoveryTypeOptions.find((i) => i.key === item.discoveryType) || {})
            .text
        }}
      </td>
    </template>
    <template v-slot:ipRangeType="{ item }">
      <td class="text-ellipsis">
        {{
          (ipRangeTypeOptions.find((i) => i.key === item.ipRangeType) || {})
            .text
        }}
      </td>
    </template>
    <template v-slot:totalCount="{ item }">
      <td class="text-ellipsis">
        {{ item.totalCount }}
      </td>
    </template>
    <template v-slot:successRecordCount="{ item }">
      <td class="text-ellipsis">
        {{ item.successRecordCount }}
      </td>
    </template>
    <template v-slot:failedRecordCount="{ item }">
      <td class="text-ellipsis">
        {{ item.failedRecordCount }}
      </td>
    </template>
    <template v-slot:importFileType="{ item }">
      <td class="text-ellipsis">
        {{ $tc(item.importFileType) }}
      </td>
    </template>
    <template v-slot:actions="{ item }">
      <td>
        <div v-if="item.importFileType !== 'mpp'">
          <FlotoDownloadLink
            v-if="
              item.status === 'success' ||
              item.status === 'failed' ||
              item.status === 'partially_processed'
            "
            :file-name="handleDownloadReport(item)['fileName']"
            :url="handleDownloadReport(item)['url']"
          >
            <template v-slot="{ downloading }">
              <MIcon
                v-if="!downloading"
                name="download"
                class="mr-2 cursor-pointer"
              ></MIcon>
            </template>
          </FlotoDownloadLink>
        </div>
        <div v-else>
          <span v-if="item.message">
            <ViewError :message="item.message" :status="item.status" />
          </span>
          <span v-else>
            <MIcon name="eye" class="mr-2 cursor-pointer text-neutral center" />
          </span>
        </div>
      </td>
    </template>
  </FlotoCrudContainer>
</template>

<script>
import Moment from 'moment'
import ImportCsvContainer from '@components/import-csv/import-csv-container'
import ViewError from './view-error-drawer'
import { getCsvBatchesApi } from '../import-csv-batch-api'
import { getAssetTypeSystemName } from '@modules/asset/helpers/asset-type'
import { authComputed } from '@state/modules/auth'
import { importCsvStatusOptions } from '../helpers/option-map'
import {
  discoveryTypeOptions,
  ipRangeTypeOptions,
} from '@modules/asset-management/helpers/option-map'
export default {
  name: 'CsvBatchList',
  components: { ImportCsvContainer, ViewError },
  data() {
    this.getAssetTypeSystemName = getAssetTypeSystemName
    this.importCsvStatusOptions = importCsvStatusOptions()
    this.discoveryTypeOptions = discoveryTypeOptions()
    this.ipRangeTypeOptions = ipRangeTypeOptions()
    return {
      moduleName: this.isPortalLogin
        ? this.$constants.REQUEST
        : this.$route.params.moduleName,
    }
  },
  computed: {
    ...authComputed,
    tableColumns() {
      return [
        {
          name: `${this.$tc('batch')} ${this.$tc('number')}`,
          key: 'batchNumber',
          width: '130px',
        },
        {
          name: `${this.$tc('created_by')}`,
          key: 'createdBy',
          width: '130px',
        },
        { name: `${this.$tc('status')}`, key: 'status', width: '150px' },
        {
          name: `${this.$tc('created_date')}`,
          key: 'createdTime',
          width: '200px',
        },
        { name: `${this.$tc('end_date')}`, key: 'endTime', width: '200px' },
        {
          name: `${this.$tc('processing_time')}`,
          key: 'processingTime',
          width: '150px',
        },
        ...(this.moduleName === this.$constants.CREDENTIAL
          ? [
              {
                name: `${this.$tc('connection_type')}`,
                key: 'discoveryType',
                width: '150px',
              },
            ]
          : []),
        ...(this.moduleName === this.$constants.IP_NETWORK
          ? [
              {
                name: `${this.$tc('connection_type')}`,
                key: 'ipRangeType',
                width: '150px',
              },
            ]
          : []),
        ...(this.moduleName === this.$constants.CLOUD_NETWORK
          ? [
              {
                name: `${this.$tc('connection_type')}`,
                key: 'cloudType',
                width: '150px',
              },
            ]
          : []),
        {
          name: `${this.$tc('total')} ${this.$tc('record', 2)}`,
          key: 'totalCount',
          width: '120px',
        },
        {
          name: `${this.$tc('success')} ${this.$tc('record', 2)}`,
          key: 'successRecordCount',
          width: '150px',
        },
        {
          name: `${this.$tc('failed')} ${this.$tc('record', 2)}`,
          key: 'failedRecordCount',
          width: '50px',
        },
        {
          name: `${this.$tc('file')} ${this.$tc('type')}`,
          key: 'importFileType',
          width: '120px',
        },
        {
          name: `${this.$tc('action', 2)}`,
          key: 'actions',
          width: '120px',
        },
      ]
    },
    backLink() {
      let link = ''
      if (this.getAssetTypeSystemName[this.moduleName]) {
        link = this.$modules.getModuleRoute('asset', '', {
          params: { assetType: this.moduleName },
        })
      }
      if (this.moduleName === this.$constants.CMDB) {
        link = this.$modules.getModuleRoute('cmdb', '', {
          params: { ciType: this.moduleName },
        })
      }
      if (this.moduleName === 'contract') {
        link = this.$modules.getModuleRoute('contract', '')
      }
      if (this.moduleName === 'project') {
        link = this.$modules.getModuleRoute('project', '')
      }
      if (
        this.moduleName === this.$constants.PRODUCT ||
        this.moduleName === this.$constants.PRODUCTVENDOR
      ) {
        link = this.$modules.getModuleRoute(
          'asset-management',
          'product-catalog'
        )
      }
      if (this.moduleName === 'vendor') {
        link = this.$modules.getModuleRoute(
          'asset-management',
          'vendor-catalog'
        )
      }
      if (this.moduleName === 'manufacturer') {
        link = this.$modules.getModuleRoute(
          'asset-management',
          'manufacturer-catalog'
        )
      }
      if (this.moduleName === 'requester' || this.moduleName === 'technician') {
        link = this.$modules.getModuleRoute('users', '', {
          params: { userType: this.moduleName },
        })
      }
      if (this.moduleName === 'department') {
        link = this.$modules.getModuleRoute('organization', 'department')
      }
      if (this.moduleName === 'location') {
        link = this.$modules.getModuleRoute('organization', 'location')
      }
      if (this.moduleName === 'software_license') {
        link = this.$modules.getModuleRoute('software-license', '')
      }
      if (
        ['snmp_property_device', 'snmp_property'].indexOf(this.moduleName) >= 0
      ) {
        link = this.$modules.getModuleRoute(
          'asset-management',
          'snmp-custom-properties'
        )
      }
      if (
        [
          'credential',
          'ip_network',
          'cloud_network',
          'private_cloud_network',
        ].indexOf(this.moduleName) >= 0
      ) {
        link = this.$modules.getModuleRoute('asset-management', 'discovery')
      }
      if (
        (this.moduleName === this.$constants.REQUEST ||
          this.moduleName === this.$constants.PROBLEM ||
          this.moduleName === this.$constants.CHANGE ||
          this.moduleName === this.$constants.RELEASE) &&
        this.$route.query.type === 'category'
      ) {
        link = this.$modules.getModuleRoute('category', undefined, {
          params: { moduleName: this.moduleName },
          query: { type: this.moduleName },
        })
      }
      if (
        this.moduleName === this.$constants.REQUEST &&
        this.$route.query.type !== 'category' &&
        !this.isPortalLogin
      ) {
        link = this.$modules.getModuleRoute('ticket', undefined, {
          params: { ticketType: this.moduleName },
        })
      }
      if (this.moduleName === this.$constants.SERVICE_CATALOG) {
        link = this.$modules.getModuleRoute('ticket', undefined, {
          params: { ticketType: this.$constants.REQUEST },
        })
      }
      if (this.moduleName === this.$constants.REQUEST && this.isPortalLogin) {
        link = this.$modules.getModuleRoute('support-portal', 'my-requests')
      }
      if (this.moduleName === this.$constants.TASK) {
        link = this.$modules.getModuleRoute('project', 'edit', {
          params: { id: this.$route.query.id },
        })
      }
      return link
    },
  },
  created() {
    this.moduleName = this.isPortalLogin
      ? this.$constants.REQUEST
      : this.$route.params.moduleName
  },
  methods: {
    getCsvBatch(limit, offset) {
      let modName =
        this.moduleName === 'requester' || this.moduleName === 'technician'
          ? this.$constants.USER
          : [
              this.$constants.REQUEST,
              this.$constants.PROBLEM,
              this.$constants.CHANGE,
              this.$constants.RELEASE,
            ].indexOf(this.moduleName) >= 0 &&
            this.$route.query.type === 'category'
          ? this.$constants.CATEGORY
          : this.moduleName
      let filter = {
        userType: this.moduleName,
        projectId: this.$route.query.id,
      }
      return getCsvBatchesApi(modName, filter, limit, offset)
    },
    handleDownloadReport(item) {
      const ext = item.importFileType === 'csv' ? 'csv' : 'xlsx'
      return {
        url: `download/csv/report/${item.id}`,
        fileName: `${item.batchNumber}_${this.$tc(
          item.moduleName
        )}_${Moment().format('D-M-YYYY_H-m-s_a')}.${ext}`,
      }
    },
  },
}
</script>
